import { BoundingInfo } from '@babylonjs/core/Culling/boundingInfo';
import { Matrix, Vector3 } from '@babylonjs/core/Maths';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { getByDynamicId } from '@kb3d/models/classes/kb3d-manager';
import { MeshNode } from '@kb3d/models/classes/meshes/mesh-node';
import { SpaceNode } from '@kb3d/models/classes/meshes/space-node';
import { KbVector } from '@kb3d/models/classes/primitives/kb-vector';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { KbViewer } from '../kb-viewer';
import { Kb3dBoundingBoxChangeHandler } from '@kb3d/models/classes/client-enums';

export interface BoundingBoxInfo {
    boundingInfo: BoundingInfo;
    min: KbVector;
    max: KbVector;
    center: KbVector;
}

export function toBoundingBoxInfo(binfo: BoundingInfo) {
    return {
        boundingInfo: binfo,
        min: KbVector.FromVec3(binfo.minimum),
        max: KbVector.FromVec3(binfo.maximum),
        center: KbVector.FromVec3(binfo.boundingBox.center),
    } as BoundingBoxInfo;
}

interface IVectorLike {
    x?: number;
    y?: number;
    z?: number;
}

const MAX_DIGESTS = 30;

export class BoundingBoxCache {
    constructor(private v: KbViewer) {}

    public boundingBoxInvalidated = new Subject<SpaceNode>();
    public rootBoundingBoxChanged = new Subject<BoundingBoxInfo>();
    private _digestCount = 0;
    private _db = new Map<string, BoundingBoxInfo>();
    private _watchedNodes = new Map<string, BehaviorSubject<BoundingBoxInfo | undefined>>();
    private _changedNodes = new Set<string>(); //stored dynamic id's of changed nodes

    public watchForChanges(node: SpaceNode): Observable<BoundingBoxInfo | undefined> {
        let subject = this._watchedNodes.get(node._dynamicId);

        if (!subject) {
            subject = new BehaviorSubject<BoundingBoxInfo | undefined>(undefined);
            this._watchedNodes.set(node._dynamicId, subject);
        }
        const _subject = subject;

        // This is using an internal implementation of rxjs, hence the any. But this is the only way we can add teardown logic.
        const observable = new Observable<void>(s => {
            s.next();
            return () => {
                setTimeout(() => {
                    if (_subject.observers.length === 0) {
                        _subject.complete();
                        this._watchedNodes.delete(node._dynamicId);
                    }
                });
            };
        });

        return observable.pipe(mergeMap(() => _subject));
    }

    public refreshAndGet(node: SpaceNode) {
        this._db.delete(node._dynamicId);
        return this.get(node);
    }

    public get(node?: SpaceNode) {
        if (node && node.visible) {
            let bb = this._db.get(node._dynamicId);
            if (!bb) {
                let bbInfo: BoundingInfo | undefined;
                const bnode = this.v.rendererManager.mesh.get3d(node);
                if (node instanceof MeshNode) {
                    if (bnode) {
                        bbInfo = (bnode as AbstractMesh).getBoundingInfo();
                    }
                } else {
                    const childNodes = node.getSpaceChildren();
                    const min: IVectorLike = {};
                    const max: IVectorLike = {};
                    let empty = true;

                    for (const c of childNodes) {
                        const cbb = this.get(c);
                        // If cbb is undefined, that means the child node is not visible or doesn't exist
                        if (cbb) {
                            empty = false;
                            const parentMin = cbb.min.toVec3();
                            const parentMax = cbb.max.toVec3();
                            const childBNode = this.v.rendererManager.mesh.get3d(c);
                            if (childBNode) {
                                const mat = Matrix.Compose(
                                    childBNode.scaling,
                                    childBNode.rotationQuaternion!,
                                    childBNode.position
                                );
                                Vector3.TransformCoordinatesToRef(parentMin, mat, parentMin);
                                Vector3.TransformCoordinatesToRef(parentMax, mat, parentMax);
                            }

                            if (min.x == null) min.x = parentMin.x;
                            if (min.y == null) min.y = parentMin.y;
                            if (min.z == null) min.z = parentMin.z;
                            if (max.x == null) max.x = parentMax.x;
                            if (max.y == null) max.y = parentMax.y;
                            if (max.z == null) max.z = parentMax.z;

                            min.x = Math.min(min.x, parentMin.x, parentMax.x);
                            min.y = Math.min(min.y, parentMin.y, parentMax.y);
                            min.z = Math.min(min.z, parentMin.z, parentMax.z);
                            max.x = Math.max(max.x, parentMin.x, parentMax.x);
                            max.y = Math.max(max.y, parentMin.y, parentMax.y);
                            max.z = Math.max(max.z, parentMin.z, parentMax.z);
                        }
                    }

                    if (empty) {
                        return undefined; //don't consider empty groups
                    }

                    bbInfo = new BoundingInfo(
                        new Vector3(min.x || 0, min.y || 0, min.z || 0),
                        new Vector3(max.x || 0, max.y || 0, max.z || 0)
                    );
                }

                if (bbInfo) {
                    bb = toBoundingBoxInfo(bbInfo);
                    this._db.set(node._dynamicId, bb);
                }
            }
            return bb;
        } else {
            return undefined;
        }
    }

    public invalidate(node: SpaceNode | undefined) {
        if (node) {
            //when a node is invalidated, all of it's ancestors need to be invalidated
            let n = node;
            while (n && n instanceof SpaceNode) {
                this._changedNodes.add(n._dynamicId);
                n = n._parent as SpaceNode;
            }
        }
    }

    public update() {
        let boundingBoxesStable = true as null | boolean;

        if (this._changedNodes.size === 0) {
            return;
        }

        if(!this.v.userIsInteracting()) {
            this._digestCount++;
        }

        const watchersToUpdateAndClear = new Map<SpaceNode, Kb3dBoundingBoxChangeHandler>();

        for (const dynamicId of this._changedNodes) {
            const n = getByDynamicId<SpaceNode>(dynamicId);
            if (!n) {
                continue;
            }
            const externalWatchers = n._manager?.boundingboxChangeHandlers.getByKey(n);
            const hasRootWatchers =
                this.v.sceneNode._dynamicId === n._dynamicId && this.rootBoundingBoxChanged.observers.length > 0;
            const hasWatchers =
                this._watchedNodes.has(n._dynamicId) || (externalWatchers !== undefined && externalWatchers.length > 0);

            const oldBoundingBox = this._db.get(n._dynamicId);
            this._db.delete(n._dynamicId);
            this.boundingBoxInvalidated.next(n);

            if (!hasWatchers && !hasRootWatchers) {
                continue;
            }

            const bbInfo = this.get(n);
            const boundingBoxChanged = !boundingBoxesMatch(bbInfo, oldBoundingBox);
            const bbWatchersSubject = this._watchedNodes.get(n._dynamicId);

            if (boundingBoxChanged) {
                boundingBoxesStable = false;
            }

            if (bbWatchersSubject) {
                bbWatchersSubject.next(bbInfo);
            }
            if (externalWatchers && bbInfo) {
                for (const w of externalWatchers) {
                    const once = w.mode === 'once';
                    // Always call the handler if the mode is 'once', because it will be expecting an update regardless of whether it changed or not.
                    // For persistent watchers, only call it if the bb actually changed.
                    if (boundingBoxChanged && !once) {
                        w.handler(bbInfo);
                    }
                    if (once) {
                        watchersToUpdateAndClear.set(n, w);
                    }
                }
            }

            if (hasRootWatchers && bbInfo && boundingBoxChanged) {
                this.rootBoundingBoxChanged.next(bbInfo);
            }
        }

        if (boundingBoxesStable && this._digestCount > 3) {
            this._changedNodes.clear();
            for (const [node, watcher] of watchersToUpdateAndClear.entries()) {
                const bbInfo = this.get(node);
                if (bbInfo) {
                    watcher.handler(bbInfo);
                }
                node._manager?.boundingboxChangeHandlers.deleteByKeyValue(node, watcher);
            }
            watchersToUpdateAndClear.clear();
            this._digestCount = 0;
        } else if (!boundingBoxesStable && this._digestCount > MAX_DIGESTS) {
            console.error('Bounding box digest cycle exceeded, but the meshes are not consistent');
            this._changedNodes.clear();
            watchersToUpdateAndClear.clear();
            this._digestCount = 0;
        }
    }

    /**
     * Gets the world bounding box for the specified node.
     * Be aware that this call is not cached, so use accordingly for performance reasons
     * @param node
     */
    public getWorld(node?: SpaceNode) {
        if (node && node.visible) {
            const bb = this.get(node)!;
            if (!bb) {
                return null;
            }
            const bnode = this.v.rendererManager.mesh.get3d(node);
            const bbInfo = new BoundingInfo(
                new Vector3(bb.min.x || 0, bb.min.y || 0, bb.min.z || 0),
                new Vector3(bb.max.x || 0, bb.max.y || 0, bb.max.z || 0),
                bnode?.getWorldMatrix()
            );
            return {
                boundingInfo: bbInfo,
                min: KbVector.FromVec3(bbInfo.boundingBox.minimumWorld),
                max: KbVector.FromVec3(bbInfo.boundingBox.maximumWorld),
                center: KbVector.FromVec3(bbInfo.boundingBox.centerWorld),
            } as BoundingBoxInfo;
        }
        return null;
    }

    public dispose() {
        this._db.clear();
        this._watchedNodes.forEach(subject => {
            subject.complete();
        });
        this._watchedNodes.clear();
    }
}

function boundingBoxesMatch(a: BoundingBoxInfo | undefined, b: BoundingBoxInfo | undefined) {
    if (!a && !b) {
        return true;
    }
    if (!a || !b) {
        return false;
    }

    return a.center.equal(b.center) && a.min.equal(b.min) && a.max.equal(b.max);
}
